import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslations } from "next-intl";
import { apiFetch } from "@/services/http/api-fetch";
import { refreshSession } from "@/services/http/client";
import { apiErrorText, readApiErrorResponse } from "@/lib/api-error";
import { signInPath } from "@/lib/session-activity";

type Props = {
  tenantSlug: string;
  token: string;
};

type Status = "loading" | "success" | "error" | "signInAgain";

export function InviteRedeemPanel({ tenantSlug, token }: Props) {
  const navigate = useNavigate();
  const t = useTranslations("invite");
  const tRoot = useTranslations();
  const [status, setStatus] = useState<Status>("loading");
  const [message, setMessage] = useState<string | null>(null);
  const teachingPath = `/t/${tenantSlug}/instructor`;

  useEffect(() => {
    let cancelled = false;

    async function redeem() {
      try {
        const res = await apiFetch(`/v1/invites/${encodeURIComponent(token)}/redeem`, {
          method: "POST",
        });
        if (!res.ok) throw await readApiErrorResponse(res);
        const json = (await res.json()) as {
          data?: { courseSlug?: string | null; enrolled?: boolean; roleChanged?: boolean };
        };

        if (cancelled) return;
        setStatus("success");

        // A staff invite raised the role on the server; re-issue the session so the new role
        // shows now. If that fails, say so instead of landing on a page the old role can't use
        // (AUTH-10).
        if (json.data?.roleChanged) {
          const renewed = await refreshSession();
          if (cancelled) return;
          if (!renewed) {
            setStatus("signInAgain");
            return;
          }
          navigate(teachingPath, { replace: true });
          return;
        }

        const courseSlug = json.data?.courseSlug;
        navigate(
          courseSlug ? `/t/${tenantSlug}/courses/${courseSlug}` : `/t/${tenantSlug}/dashboard`,
          { replace: true },
        );
      } catch (err) {
        if (cancelled) return;
        setStatus("error");
        setMessage(apiErrorText(err, tRoot, "invite.redeemFailed", { namespace: "invite.errors" }));
      }
    }

    void redeem();
    return () => {
      cancelled = true;
    };
  }, [tenantSlug, token, navigate, tRoot, teachingPath]);

  const text =
    status === "loading"
      ? t("redeeming")
      : status === "success"
        ? t("accepted")
        : status === "signInAgain"
          ? t("signInAgain")
          : message;

  return (
    <main className="mx-auto max-w-lg px-4 py-16" data-testid="invite-redeem-page">
      <h1 className="text-2xl font-semibold">{t("title")}</h1>
      <p
        role={status === "error" ? "alert" : "status"}
        className={`mt-4 ${status === "error" ? "text-[var(--danger)]" : "text-[var(--text-muted)]"}`}
        data-testid="invite-redeem-status"
      >
        {text}
      </p>
      {status === "signInAgain" && (
        <Link
          to={signInPath(tenantSlug, teachingPath)}
          className="mt-6 inline-block rounded-md bg-[var(--accent-strong)] px-4 py-2 text-sm text-white hover:bg-[var(--accent-strong-hover)]"
          data-testid="invite-sign-in-again"
        >
          {t("signInAgainLink")}
        </Link>
      )}
    </main>
  );
}
